import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { Search } from 'lucide-react';
import { api } from '../services/api';
import { theme, colorForCategory } from '../theme';
import { LoadingSpinner } from '../components/LoadingSpinner';

const tabLabels = [
  'All',
  'Core AI Intelligence',
  'Omnipresence',
  'Content & Social',
  'Paid Ads & SEO',
  'Engagement & Network',
  'Technical SEO',
  'GEO & AI Visibility',
  'Game Changers',
  'Revenue Multipliers',
  'Trust & Credibility',
  'Operations & Control',
];

type Agent = {
  id: string;
  agent_number: number;
  name: string;
  category: string;
  description: string | null;
  icon: string | null;
  is_enabled: boolean;
  status: string;
  last_run_at: string | null;
  runs_count: number;
  toggle_locked?: boolean;
  label_status?: string | null;
};

function formatLastRun(iso: string | null): string {
  if (!iso) return 'Ready to activate';
  const t = new Date(iso).getTime();
  const mins = Math.floor((Date.now() - t) / 60000);
  if (mins < 1) return 'Last run: just now';
  if (mins < 60) return `Last run: ${mins} mins ago`;
  const h = Math.floor(mins / 60);
  if (h < 24) return `Last run: ${h}h ago`;
  return `Last run: ${Math.floor(h / 24)}d ago`;
}

function statusLabel(s: string, enabled: boolean): string {
  if (!enabled) return 'Paused';
  if (s === 'running') return 'Active';
  return 'Idle';
}

function statusBadgeClass(st: string): string {
  if (st === 'Beta') return 'odc-badge-cyan';
  if (st === 'Coming Soon') return 'odc-badge-amber';
  if (st === 'Active') return 'odc-badge-green';
  if (st === 'Idle') return 'odc-badge-amber';
  return 'odc-badge-cyan';
}

function displayStatus(a: Agent): string {
  if (a.label_status) return a.label_status;
  return statusLabel(a.status, a.is_enabled);
}

export function AgentsPage() {
  const [agents, setAgents] = useState<Agent[]>([]);
  const [bizId, setBizId] = useState<string | null>(null);
  const [tab, setTab] = useState('All');
  const [q, setQ] = useState('');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    void (async () => {
      try {
        const { data: b } = await api.get<{ business: { id: string } | null }>('/business/me');
        if (!b.business) {
          setLoading(false);
          return;
        }
        setBizId(b.business.id);
        const { data } = await api.get<{ agents: Agent[] }>(`/agents/business/${b.business.id}`);
        setAgents(
          data.agents.map((a) => ({
            ...a,
            last_run_at: a.last_run_at
              ? typeof a.last_run_at === 'string'
                ? a.last_run_at
                : (a.last_run_at as unknown as Date).toISOString?.() || null
              : null,
          }))
        );
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const filtered = useMemo(() => {
    return agents.filter((a) => {
      if (tab !== 'All' && a.category !== tab) return false;
      if (!q.trim()) return true;
      const s = q.toLowerCase();
      return (
        a.name.toLowerCase().includes(s) ||
        a.category.toLowerCase().includes(s) ||
        String(a.agent_number).includes(s)
      );
    });
  }, [agents, tab, q]);

  const counts = useMemo(() => {
    let active = 0;
    let idle = 0;
    let paused = 0;
    for (const a of agents) {
      const st = statusLabel(a.status, a.is_enabled);
      if (st === 'Active') active++;
      else if (st === 'Idle') idle++;
      else paused++;
    }
    return { active, idle, paused };
  }, [agents]);

  async function toggle(agentId: string, enabled: boolean) {
    if (!bizId) return;
    try {
      await api.post('/agents/toggle', { businessId: bizId, agentId, enabled });
    } catch {
      return;
    }
    setAgents((prev) =>
      prev.map((a) =>
        a.id === agentId
          ? {
              ...a,
              is_enabled: enabled,
              status: enabled ? 'idle' : 'paused',
            }
          : a
      )
    );
  }

  if (loading) return <LoadingSpinner />;
  if (!bizId) {
    return (
      <div
        className="odc-page relative z-10 mx-auto max-w-lg space-y-6 rounded-2xl p-10 text-center odc-card"
        style={{ transform: 'none' }}
      >
        <p style={{ color: 'var(--muted)' }}>Create a business first to manage your agent fleet.</p>
        <Link to="/onboarding" className="odc-btn-primary inline-flex justify-center">
          Complete onboarding
        </Link>
      </div>
    );
  }

  return (
    <div className="odc-page relative z-10 space-y-8">
      <header className="space-y-3">
        <div className="inline-flex items-center gap-2 rounded-full border px-3 py-1" style={{ borderColor: 'var(--border2)', background: 'var(--card)' }}>
          <span className="text-[10px] font-bold uppercase tracking-[0.18em]" style={{ color: theme.primary }}>
            Your Fleet
          </span>
        </div>
        <div>
          <h1 className="text-2xl font-bold md:text-3xl" style={{ color: 'var(--text)' }}>
            Your AI Agent Fleet
          </h1>
          <p className="mt-1 max-w-xl text-sm" style={{ color: 'var(--muted)' }}>
            Powerful AI specialists working for your business — filter by category, search by name or number, toggle on or off from one grid.
          </p>
        </div>
      </header>

      <div className="grid gap-3 sm:grid-cols-3">
        {[
          ['Active', counts.active, 'odc-badge-green'],
          ['Idle', counts.idle, 'odc-badge-amber'],
          ['Paused', counts.paused, 'odc-badge-cyan'],
        ].map(([label, n, badge]) => (
          <div
            key={String(label)}
            className="odc-stat-card flex items-center justify-between gap-4 !py-4"
            style={{ animation: 'none' }}
          >
            <span className={`odc-badge ${badge}`}>{label}</span>
            <p className="odc-metric !text-2xl" style={{ color: 'var(--text)' }}>
              {n as number}
            </p>
          </div>
        ))}
      </div>

      <div className="odc-card !p-4 sm:!p-5" style={{ transform: 'none' }}>
        <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
          <div className="flex min-w-0 flex-1 flex-wrap gap-2">
            {tabLabels.map((t) => {
              const col = t === 'All' ? theme.primary : colorForCategory(t);
              const active = tab === t;
              return (
                <button
                  key={t}
                  type="button"
                  className={`odc-pill-tab ${active ? 'active' : ''}`}
                  style={
                    active
                      ? { borderColor: col, background: `${col}24`, color: 'var(--text)' }
                      : undefined
                  }
                  onClick={() => setTab(t)}
                >
                  {t === 'All' ? t : t.split(' ')[0]}
                </button>
              );
            })}
          </div>
          <div className="relative max-w-md flex-1">
            <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2" style={{ color: 'var(--muted)' }} />
            <input
              className="odc-input !pl-10"
              placeholder="Search agents by name or category..."
              value={q}
              onChange={(e) => setQ(e.target.value)}
              aria-label="Search agents"
            />
          </div>
        </div>
      </div>

      {filtered.length === 0 ? (
        <div className="odc-card !p-12 text-center" style={{ transform: 'none' }}>
          <p className="font-medium" style={{ color: 'var(--text)' }}>
            No agents match
          </p>
          <p className="mt-2 text-sm" style={{ color: 'var(--muted)' }}>
            Try another category or clear the search.
          </p>
        </div>
      ) : (
        <div className="odc-agents-grid grid gap-4 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 2xl:grid-cols-5">
          {filtered.map((a) => {
            const catC = colorForCategory(a.category);
            const st = displayStatus(a);
            const locked = !!a.toggle_locked;
            const NEW_AGENT_BAND_MAX = 90;
            const isNewRibbon = a.agent_number >= 81 && a.agent_number <= NEW_AGENT_BAND_MAX;
            return (
              <div
                key={a.id}
                className="odc-card relative flex flex-col overflow-hidden !p-5 transition hover:border-[var(--border2)]"
                style={{ transform: 'none' }}
              >
                {isNewRibbon ? (
                  <span
                    className="absolute right-0 top-0 z-[1] origin-top-right translate-x-2 -translate-y-0.5 rotate-45 bg-gradient-to-r from-emerald-500 to-cyan-500 px-6 py-1 text-[9px] font-black uppercase tracking-wider text-white shadow-md"
                    style={{ boxShadow: '0 4px 14px rgba(16,185,129,0.35)' }}
                  >
                    New
                  </span>
                ) : null}
                <div className="flex items-start justify-between gap-2">
                  <span
                    className="rounded-lg px-2 py-0.5 text-[10px] font-black"
                    style={{ background: `${catC}30`, color: catC }}
                  >
                    #{String(a.agent_number).padStart(2, '0')}
                  </span>
                  <button
                    type="button"
                    role="switch"
                    aria-checked={a.is_enabled}
                    disabled={locked}
                    aria-label={`${a.is_enabled ? 'Disable' : 'Enable'} ${a.name}`}
                    className={`odc-toggle shrink-0 ${a.is_enabled ? 'active' : ''} ${locked ? 'cursor-not-allowed opacity-40' : ''}`}
                    onClick={() => {
                      if (locked) return;
                      void toggle(a.id, !a.is_enabled);
                    }}
                  />
                </div>
                <div className="mt-3 text-center text-4xl leading-none odc-float">{a.icon || '🤖'}</div>
                <h3 className="mt-2 text-center text-sm font-bold" style={{ color: 'var(--text)' }}>
                  {a.name}
                </h3>
                <p className="mt-1 text-center text-[10px] font-bold uppercase tracking-wide" style={{ color: catC }}>
                  {a.category}
                </p>
                <p className="mt-2 line-clamp-2 min-h-[2.5rem] text-center text-xs" style={{ color: 'var(--muted)' }}>
                  {a.description || '—'}
                </p>
                <span className={`odc-badge ${statusBadgeClass(st)} mx-auto mt-3`}>{st}</span>
                <p className="mt-2 text-center text-[10px]" style={{ color: 'var(--muted)' }}>
                  {formatLastRun(a.last_run_at)}
                </p>
                <button type="button" className="odc-btn-ghost mt-4 w-full justify-center !py-2.5 text-xs" disabled>
                  Configure (soon)
                </button>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
